import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import BriaDropDown, { DropDownItem } from "../../../../../../../components/common/BriaDropDown/BriaDropDown.tsx";
import ConfirmationPopup from "../../../../../../../components/common/ConfirmationPopup/ConfirmationPopup.tsx";
import { useAppStore } from "../../../../../../../hooks/useStores.tsx";
import { TgModel } from "../../../../../Models/store/tg-model-store.tsx";

interface CheckpointDropdownProps {
	model: TgModel;
	setShowChangeVersionPopup: (
		popup: {
			model: TgModel;
			currentCheckpointStep: string;
			checkpointStepToBeChanged: string;
			setSelectedCheckpoint: any;
		} | null,
	) => void;
}

const CheckpointDropdown = ({ model, setShowChangeVersionPopup }: CheckpointDropdownProps) => {
	const [checkpoints, setCheckpoints] = useState<DropDownItem<string, any>[]>([]);
	const [selectedCheckpoint, setSelectedCheckpoint] = useState<string>(model.active_model_version || "");

	useEffect(() => {
		setCheckpoints(
			model.checkpoint_steps
				?.filter((cp: any) => cp != selectedCheckpoint)
				.map((cp: any) => ({
					key: cp,
					value: cp,
				})) || [],
		);
	}, []);

	const handleCheckpointClick = (newCheckpoint: string) => {
		setShowChangeVersionPopup({
			model: model,
			currentCheckpointStep: model.active_model_version ?? "",
			checkpointStepToBeChanged: newCheckpoint,
			setSelectedCheckpoint,
		});
	};

	return (
		<BriaDropDown
			value={selectedCheckpoint}
			items={checkpoints}
			disabled={!checkpoints.length}
			onChange={(e) => handleCheckpointClick(e.target.value)}
			renderValue={(selected: string) => {
				return selected;
			}}
		/>
	);
};

interface ChangeVersionPopupProps {
	showChangeVersionPopup: {
		model: TgModel;
		currentCheckpointStep: string;
		checkpointStepToBeChanged: string;
		setSelectedCheckpoint: any;
	} | null;
	setShowChangeVersionPopup: (
		popup: {
			model: TgModel;
			currentCheckpointStep: string;
			checkpointStepToBeChanged: string;
			setSelectedCheckpoint: any;
		} | null,
	) => void;
}

export const ChangeVersionPopup = ({ showChangeVersionPopup, setShowChangeVersionPopup }: ChangeVersionPopupProps) => {
	const { t } = useTranslation("tg", { keyPrefix: "models.dashboard" });
	const { tgStore } = useAppStore();
	const { tgModelStore } = tgStore;

	const handleChangeVersion = async () => {
		if (showChangeVersionPopup) {
			const newTgModel = showChangeVersionPopup.model;
			newTgModel.active_model_version = showChangeVersionPopup.checkpointStepToBeChanged;

			await tgModelStore.updateModel(showChangeVersionPopup.model.id, showChangeVersionPopup.model);
			showChangeVersionPopup.setSelectedCheckpoint(showChangeVersionPopup.checkpointStepToBeChanged);
			setShowChangeVersionPopup(null);
		}
	};

	return (
		<ConfirmationPopup
			title={t("changeVersionPopup.title")}
			description={t("changeVersionPopup.description", {
				checkpoint: showChangeVersionPopup?.currentCheckpointStep,
			})}
			subDescription={t("changeVersionPopup.confirmationMessage")}
			confirmButtonText={t("changeVersionPopup.button")}
			boldSubDescription
			open={showChangeVersionPopup != null}
			onClose={() => setShowChangeVersionPopup(null)}
			onClick={handleChangeVersion}
		/>
	);
};

export default CheckpointDropdown;
